import { ContactDetails } from '@Components/AccountPage/Organization/ContactDetails';
import { KeyPeople } from '@Components/AccountPage/Organization/KeyPeople';
import { LegalDetails } from '@Components/AccountPage/Organization/LegalDetails';
import { Button } from '@Components/common';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from './OrganizationController.module.scss';
import { KeyPersonForm } from '@Components/AccountPage/Organization/KeyPersonForm';
import { KeyPerson, KeyPersonRole } from '@Types/KeyPeople';
import { useNotificationsContext, useUserContext } from '@Contexts';
import { useGetDueDiligenceDetails, useUpdateDueDiligenceDetails } from '@Hooks/Api';
import { OrganizationFormType } from '@Types/OrganizationDueDiligenceData';
import { Loader } from '@Components';
import { UserTypeEnum } from '@Types';

export const OrganizationController = ({ afterSubmit }: { afterSubmit?: () => void }) => {
  const { t } = useTranslation('account');
  const [keyPersonFormOpen, setKeyPersonFormOpen] = useState(false);
  const [keyPeople, setKeyPeople] = useState<KeyPerson[]>([]);
  const [keyPerson, setKeyPerson] = useState<KeyPerson>();
  const { success: successNotification } = useNotificationsContext();
  const methods = useForm<OrganizationFormType>({
    shouldUnregister: false,
  });

  const { reset, setError, errors, clearErrors } = methods;

  const { userData } = useUserContext();
  const [getDueDiligenceData, { data: dueDiligenceData, loading: dueDiligenceDataLoading }] =
    useGetDueDiligenceDetails();
  const [
    updateDueDiligenceData,
    { loading: isUpdateDueDiligenceDataLoading, error: UpdateDueDiligenceDataError },
  ] = useUpdateDueDiligenceDetails();
  const organizationId = userData?.organization_id;
  const isGroup = userData?.user_type === UserTypeEnum.GROUP;

  useEffect(() => {
    if (organizationId) {
      getDueDiligenceData(organizationId).then(({ data }) => {
        setKeyPeople(data.key_people.people);

        const formattedData = {
          ...data.registered_details,
          all_people_submitted: data.key_people.all_people_submitted,
        };

        reset(formattedData);
      });
    }
  }, [organizationId, getDueDiligenceData, reset]);

  const ownerCount = useMemo(() => {
    return keyPeople.filter((person) => person.is_owner).length;
  }, [keyPeople]);

  const representativeCount = useMemo(() => {
    return keyPeople.filter((person) => person.is_representative).length;
  }, [keyPeople]);

  const directorCount = useMemo(() => {
    return keyPeople.filter((person) => person.is_director).length;
  }, [keyPeople]);

  const executiveCount = useMemo(() => {
    return keyPeople.filter((person) => person.is_executive).length;
  }, [keyPeople]);

  const keyPeopleRoles: KeyPersonRole[] = [
    {
      name: t('tabs.organization.key_people.roles.representative.name'),
      description: t('tabs.organization.key_people.roles.representative.description'),
      amount: representativeCount,
    },
    {
      name: t('tabs.organization.key_people.roles.owner.name'),
      description: t('tabs.organization.key_people.roles.owner.description'),
      amount: ownerCount,
    },
    {
      name: t('tabs.organization.key_people.roles.director.name'),
      description: t('tabs.organization.key_people.roles.director.description'),
      amount: directorCount,
    },
    {
      name: t('tabs.organization.key_people.roles.executive.name'),
      description: t('tabs.organization.key_people.roles.executive.description'),
      amount: executiveCount,
    },
  ];

  useEffect(() => {
    if (representativeCount <= 1) {
      clearErrors('representative');
    }
  }, [representativeCount, clearErrors]);

  useEffect(() => {
    if (representativeCount > 0 && executiveCount > 0 && directorCount > 0 && ownerCount > 0) {
      clearErrors('key_people');
    }
  }, [representativeCount, executiveCount, directorCount, ownerCount, clearErrors]);

  useEffect(() => {
    if (keyPersonFormOpen) {
      const modalElement = document.getElementById('key-person-modal');
      if (modalElement) {
        modalElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [keyPersonFormOpen]);

  const onSubmit = methods.handleSubmit(
    (data) => {
      if (
        (representativeCount == 0 ||
          executiveCount == 0 ||
          directorCount == 0 ||
          ownerCount == 0) &&
        data.all_people_submitted
      ) {
        methods.setError('key_people', {
          type: 'custom',
          message: t('tabs.organization.key_people.all_people_submitted_error'),
        });
        return;
      }

      if (representativeCount > 1) {
        setError('representative', {
          type: 'custom',
          message: t('tabs.organization.key_people.roles.representative.error'),
        });
        return;
      }

      if (organizationId) {
        const updatedData = {
          ...data,
          legal_name: data.legal_name || undefined,
          company_number: data.company_number || undefined,
          charity_number: data.charity_number || undefined,
        };

        updateDueDiligenceData(organizationId, {
          registered_details: updatedData,
          key_people: {
            people: keyPeople,
            all_people_submitted: updatedData.all_people_submitted || false,
          },
        }).then(() => {
          successNotification(
            t('update_success_message', { section: t('tabs.organization.title') }),
          );
          afterSubmit && afterSubmit();
        });
      }
    },
    () => {},
  );

  if (dueDiligenceDataLoading) {
    return <Loader />;
  }
  return (
    <>
      {!keyPersonFormOpen ? (
        <div className={styles.container}>
          <FormProvider {...methods}>
            <form onSubmit={onSubmit} className={styles.formContainer}>
              <LegalDetails
                typeOfOrganization={dueDiligenceData?.registered_details.type_of_organization}
              />
              {isGroup && (
                <>
                  <KeyPeople
                    setKeyPersonFormOpen={setKeyPersonFormOpen}
                    keyPeopleRoles={keyPeopleRoles}
                    keyPeople={keyPeople}
                    setKeyPerson={setKeyPerson}
                  />
                  {methods.errors.key_people && (
                    <p className={styles.errorText}>{methods.errors.key_people.message}</p>
                  )}
                  {methods.errors.representative && (
                    <p className={styles.errorText}>{methods.errors.representative.message}</p>
                  )}
                </>
              )}

              <ContactDetails />
              <Button
                buttonType={'primary'}
                className={styles.button}
                type="submit"
                loading={isUpdateDueDiligenceDataLoading}
                disabled={isUpdateDueDiligenceDataLoading}
              >
                {t('save')}
              </Button>
            </form>
          </FormProvider>
        </div>
      ) : (
        <div id="key-person-modal">
          <KeyPersonForm
            closeForm={() => setKeyPersonFormOpen(false)}
            keyPeopleRoles={keyPeopleRoles}
            keyPeople={keyPeople}
            setKeyPeople={setKeyPeople}
            keyPerson={keyPerson}
            setKeyPerson={setKeyPerson}
          />
        </div>
      )}
    </>
  );
};
